/**
 * Super Admin Owners — list every brand owner with their brand binding,
 * reset password, deactivate / reactivate.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, UserCheck, UserX, UserCog, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { FieldRow, Input } from '@/components/ui_primitives/Field';
import { confirm } from '@/components/ui_primitives/confirm';

export default function AdminOwnersPage() {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const ownersQ = useQuery({
    queryKey: qk.adminOwners(),
    queryFn: () => api.adminOwners(token),
    enabled: !!token,
  });
  const brandsQ = useQuery({
    queryKey: qk.adminBrands(),
    queryFn: () => api.adminBrands(token),
    enabled: !!token,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.adminOwners() });
  const toggle = useMutation({
    mutationFn: ({ id, on }) =>
      on ? api.adminActivateOwner(token, id) : api.adminDeactivateOwner(token, id),
    onSuccess: (_d, v) => { toast.success(v.on ? 'Owner activated' : 'Owner deactivated'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Action failed')),
  });

  if (ownersQ.isLoading) return <FullSpinner />;
  if (ownersQ.error) return <QueryError error={ownersQ.error} onRetry={() => ownersQ.refetch()} />;

  const items = ownersQ.data || [];
  const brandsById = new Map((brandsQ.data || []).map((b) => [b.id, b]));

  return (
    <div className="space-y-6" data-testid="admin-owners-page">
      <PageHeader
        kicker="Owners"
        title="Brand owners"
        subtitle="Manage credentials and status of every brand-owner account on the platform."
      />

      {items.length === 0 ? (
        <EmptyState icon={<UserCog size={20} />} title="No owners yet" subtitle="Onboard a brand and a primary owner from the Brands page." />
      ) : (
        <SectionCard padded={false}>
          <Table testID="admin-owners-table">
            <THead>
              <Tr>
                <Th>Owner</Th>
                <Th>Email</Th>
                <Th>Brand</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {items.map((o) => {
                const brand = brandsById.get(o.brand_id);
                return (
                  <Tr key={o.id}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 grid place-items-center text-gold font-semibold text-xs">
                          {(o.name ?? '?').slice(0, 1).toUpperCase()}
                        </div>
                        <div className="font-medium">{o.name}</div>
                      </div>
                    </Td>
                    <Td><span className="font-mono text-xs text-white/70">{o.email}</span></Td>
                    <Td>
                      {brand ? (
                        <div className="text-sm">
                          <div>{brand.name}</div>
                          <div className="text-[11px] text-white/40 font-mono">{brand.slug}</div>
                        </div>
                      ) : <span className="text-white/30">—</span>}
                    </Td>
                    <Td>
                      {o.is_active === false
                        ? <StatusPill variant="slate">Inactive</StatusPill>
                        : <StatusPill variant="gold">Active</StatusPill>}
                    </Td>
                    <Td align="right">
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" variant="subtle" leftIcon={<KeyRound size={12} />}
                          onClick={() => setResetTarget(o)} testID={`owner-reset-${o.id}`}>Reset</Button>
                        {o.is_active === false ? (
                          <Button size="sm" variant="secondary" leftIcon={<UserCheck size={12} />}
                            onClick={() => toggle.mutate({ id: o.id, on: true })}
                            loading={toggle.isPending}
                            testID={`owner-activate-${o.id}`}>Reactivate</Button>
                        ) : (
                          <Button size="sm" variant="danger" leftIcon={<UserX size={12} />}
                            onClick={async () => {
                              const ok = await confirm({
                                title: `Deactivate ${o.name}?`,
                                message: `They will be unable to sign in until reactivated.`,
                                confirmText: 'Deactivate', tone: 'danger',
                              });
                              if (ok) toggle.mutate({ id: o.id, on: false });
                            }}
                            testID={`owner-deactivate-${o.id}`}>Deactivate</Button>
                        )}
                        <Button
                          size="sm"
                          variant="danger"
                          leftIcon={<Trash2 size={12} />}
                          onClick={() => setDeleteTarget({ owner: o, brand })}
                          testID={`owner-delete-${o.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </SectionCard>
      )}

      {resetTarget ? <ResetPasswordModal owner={resetTarget} onClose={() => setResetTarget(null)} /> : null}
      {deleteTarget ? (
        <DeleteOwnerModal
          owner={deleteTarget.owner}
          brand={deleteTarget.brand}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { invalidate(); setDeleteTarget(null); }}
        />
      ) : null}
    </div>
  );
}

// Delete-owner modal — confirmation must equal the owner's brand name,
// matching the backend's `?confirm=<brand_name>` guard. Disables the
// danger button until the input matches verbatim. Brand-less owners
// (should not exist in practice) cannot be deleted from the UI — the
// backend would refuse the request anyway with confirm_mismatch.
function DeleteOwnerModal({ owner, brand, onClose, onDeleted }) {
  const token = useAuthStore((s) => s.token);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const expected = brand?.name || '';
  const matches = !!expected && confirmText === expected;

  const onConfirm = async () => {
    if (!matches) return;
    setBusy(true);
    try {
      await api.adminDeleteOwner(token, owner.id, expected);
      toast.success(`Owner ${owner.name} deleted`);
      onDeleted();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Delete failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete ${owner.name}?`}
      size="md"
      subtitle={
        brand
          ? `Soft-deletes the owner. The brand "${brand.name}" itself is kept — delete it separately if you also want it gone.`
          : 'This owner has no brand attached — deletion not possible.'
      }
      testID="owner-delete-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!matches || busy}
            loading={busy}
            onClick={onConfirm}
            testID="owner-delete-confirm"
          >
            Delete owner
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-white/70">
          The owner account will be suspended and removed from this list.
          Their brand stays untouched. Use the Brands page to soft-delete
          the brand itself.
        </p>
        <FieldRow
          label={
            <>
              Type <span className="font-mono text-gold">{expected}</span> (brand name) to confirm
            </>
          }
        >
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={expected}
            autoFocus
            disabled={!expected}
            testID="owner-delete-confirm-input"
          />
        </FieldRow>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ owner, onClose }) {
  const token = useAuthStore((s) => s.token);
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pwd.length < 6) { toast.error('Min 6 chars'); return; }
    setBusy(true);
    try {
      await api.adminResetOwnerPassword(token, owner.id, pwd);
      toast.success(`Password reset for ${owner.name}`);
      onClose();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Reset failed'));
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Reset password — ${owner.name}`} size="sm"
      testID="owner-reset-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={busy} testID="owner-reset-save">Reset</Button>
        </>
      }
    >
      <FieldRow label="New password" hint="Min 6 chars">
        <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="••••••••" />
      </FieldRow>
      <p className="text-xs text-white/40 mt-3">Share the new password with the owner securely.</p>
    </Modal>
  );
}
